// src/components/LabTest/AllPackagesAPI.js

import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');

// ✅ Fetch all packages from API (matching admin and lab services)
export const fetchAllPackagesFromAPI = async (labId = null) => {
  try {
    // 1. Try primary admin endpoint (used on Lab page)
    const adminUrl = labId && labId !== 'all'
      ? `https://admin.medrphalabs.com/api/Package?id=${labId}`
      : `https://admin.medrphalabs.com/api/Package?id=0`;

    const adminRes = await axios.get(adminUrl);
    let adminList = [];
    if (Array.isArray(adminRes.data)) {
      adminList = adminRes.data;
    } else if (Array.isArray(adminRes.data?.data)) {
      adminList = adminRes.data.data;
    }

    if (adminList.length > 0) {
      return adminList.filter(p => p.isActive !== false && p.isActive !== 0 && (p.packageName || p.name));
    }
  } catch (err) {
    console.warn("Admin package API fallback triggered:", err);
  }

  // 2. Fallback to MasterAPI
  let allPackages = [];
  let currentPageNum = 1;
  let hasMorePages = true;
  
  while (hasMorePages) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/MasterAPI/GetTestPackagesAll?pageNumber=${currentPageNum}&pageSize=100`
      );
      
      if (response.data && response.data.succeeded) {
        const data = response.data.data || [];
        const pageData = Array.isArray(data) ? data : [];
        allPackages = [...allPackages, ...pageData];
        
        const totalPagesFromAPI = response.data.totalPages || 1;
        hasMorePages = currentPageNum < totalPagesFromAPI;
        currentPageNum++;
      } else {
        hasMorePages = false;
      }
    } catch {
      hasMorePages = false;
    }
  }
  
// ✅ Filter: Only keep packages where isActive === true
  return allPackages.filter(pkg => pkg.isActive === true);
};

// ✅ Universal Package Image URL Resolver (matching Flutter Medrpha Lab)
export const getPackageImageUrl = (pkg) => {
  if (!pkg) return null;
  const raw = pkg.dynamicImageUrl || pkg.image || pkg.packageImage || pkg.PackageImage || pkg.package_image || pkg.packageImageUrl || pkg.imageUrl || pkg.Image || pkg.imagePath || pkg.thumbnail;
  if (!raw || typeof raw !== 'string') return null;
  const clean = raw.trim();
  if (!clean || clean === 'null' || clean === 'undefined') return null;
  
  if (clean.startsWith('http://') || clean.startsWith('https://')) {
    return clean;
  }
  
  const normalized = clean.replace(/^\/+/, '');
  if (normalized.startsWith('PackageImage/') || normalized.startsWith('LabImage/') || normalized.startsWith('TestImage/')) {
    return `https://admin.medrphalabs.com/${normalized}`;
  }
  
  return `https://admin.medrphalabs.com/PackageImage/${normalized}`;
};

// ✅ Get user ID from localStorage
export const getUserId = () => {
  const user = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
  return user?.userId || null;
};

// ✅ Package price calculation helper
export const calculatePackagePrice = (pkg) => {
  const finalPrice = Number(pkg.packagePrice) || 0;
  const originalPrice = Number(pkg.totalPrice) || 0;
  const discountAmount = Number(pkg.discountPrice) || 0;
  const hasDiscount = discountAmount > 0 && finalPrice < originalPrice;
  const discountPercent = originalPrice > 0 && discountAmount > 0 
    ? Math.round((discountAmount / originalPrice) * 100) 
    : 0;
  
  return {
    finalPrice,
    originalPrice,
    discountAmount,
    hasDiscount,
    discountPercent
  };
};

// ✅ Get tests count from package
export const getTestsCount = (pkg) => {
  if (!pkg.testPackageDetailsJson) return 0;
  try {
    const tests = JSON.parse(pkg.testPackageDetailsJson);
    return Array.isArray(tests) ? tests.length : 0;
  } catch {
    return 0;
  }
};

// ✅ Get unique labs from packages
export const getUniqueLabs = (packages) => {
  return [...new Map(
    packages.filter(p => p.labId && p.labName)
      .map(pkg => [pkg.labId, pkg.labName])
  ).entries()].map(([id, name]) => ({ id, name }));
};

// ✅ Get unique companies from packages
export const getUniqueCompanies = (packages) => {
  return [...new Map(
    packages.filter(p => p.companyId && p.companyName)
      .map(pkg => [pkg.companyId, pkg.companyName])
  ).entries()].map(([id, name]) => ({ id, name }));
};

// ✅ Filter and sort packages
export const filterAndSortPackages = (packages, filters) => {
  let result = [...packages];

  const { searchTerm, selectedLab, selectedCompany, sortBy, priceRange } = filters;

  if (searchTerm) {
    result = result.filter(pkg =>
      pkg.packageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.labName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  if (selectedLab !== 'all') {
    result = result.filter(pkg => pkg.labId === parseInt(selectedLab));
  }

  if (selectedCompany !== 'all') {
    result = result.filter(pkg => pkg.companyId === parseInt(selectedCompany));
  }

  result = result.filter(pkg => 
    pkg.packagePrice >= priceRange.min && 
    pkg.packagePrice <= priceRange.max
  );

  switch (sortBy) {
    case 'popular':
      result.sort((a, b) => (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0));
      break;
    case 'price-low':
      result.sort((a, b) => (a.packagePrice || 0) - (b.packagePrice || 0));
      break;
    case 'price-high':
      result.sort((a, b) => (b.packagePrice || 0) - (a.packagePrice || 0));
      break;
    case 'discount':
      result.sort((a, b) => (b.discountPrice || 0) - (a.discountPrice || 0));
      break;
    case 'name':
      result.sort((a, b) => a.packageName?.localeCompare(b.packageName) || 0);
      break;
    default:
      break;
  }

  return result;
};